import { useState, useEffect, useCallback } from 'react';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { useAuth } from '../context/AuthContext';
import {
  getCurrentCounts,
  getDowngradeRecommendations,
  createDowngradeSnapshot,
  pauseUnselectedContent,
  DowngradeSelections,
  DowngradeRecommendations,
} from '../services/downgradeService';
import {
  comparePlans,
  PlanComparisonResult,
  FeatureComparison,
} from '../services/planComparisonService';

/**
 * @fileoverview Hook para manejar el wizard de downgrade de plan
 */

export interface WizardStep {
  id: string;
  title: string;
  description: string;
  component: 'welcome' | 'groups' | 'members' | 'activities' | 'checklists' | 'recipes' | 'shopping' | 'blocked' | 'summary';
  canSkip: boolean;
  isRequired: boolean;
}

export function useDowngradeWizard(fromPlanId: string, toPlanId: string = 'free') {
  const { activeFamily } = useActiveFamily();
  const { user } = useAuth();
  
  // Estado del wizard
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Datos de comparación
  const [comparison, setComparison] = useState<PlanComparisonResult | null>(null);
  const [currentCounts, setCurrentCounts] = useState<Record<string, number>>({});
  const [recommendations, setRecommendations] = useState<DowngradeRecommendations | null>(null);
  
  // Selecciones del usuario
  const [selections, setSelections] = useState<DowngradeSelections>({
    activeActivityIds: [],
    activeChecklistIds: [],
    activeRecipeIds: [],
  });
  
  // Pasos del wizard (dinámicos según la comparación)
  const [steps, setSteps] = useState<WizardStep[]>([]);
  
  /**
   * Inicializa el wizard con la comparación de planes
   */
  const initialize = useCallback(async () => {
    if (!activeFamily?.id || !user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Obtener conteos actuales
      const counts = await getCurrentCounts(activeFamily.id);
      setCurrentCounts(counts);
      
      // Comparar planes
      const comp = await comparePlans(fromPlanId, toPlanId, counts);
      
      if (!comp) {
        throw new Error('No se pudo comparar los planes');
      }
      
      setComparison(comp);
      
      // Obtener recomendaciones
      if (comp.requiresWizard) {
        const recs = await getDowngradeRecommendations(activeFamily.id, {
          activities: comp.toPlan.limits.activities || 0,
          checklists: comp.toPlan.limits.checklists || 0,
          recipes: comp.toPlan.limits.recipes || 0,
        });
        setRecommendations(recs);
        
        // Pre-seleccionar recomendaciones
        setSelections({
          activeActivityIds: recs.activityIds,
          activeChecklistIds: recs.checklistIds,
          activeRecipeIds: recs.recipeIds,
          activeShoppingListId: recs.shoppingListId,
        });
      }
      
      // Construir pasos dinámicamente
      const wizardSteps: WizardStep[] = [
        {
          id: 'welcome',
          title: 'Cambio de Plan',
          description: 'Información sobre tu cambio de plan',
          component: 'welcome',
          canSkip: false,
          isRequired: true,
        },
      ];
      
      console.log('📊 TODOS los cambios detectados:', comp.changes);
      console.log('📊 Current counts:', counts);
      
      // Agregar paso de grupos/familias si necesita selección
      const familiesChange = comp.changes.find(c => c.feature === 'families');
      console.log('👨‍👩‍👧‍👦 Families change:', familiesChange);
      if (familiesChange?.needsSelection) {
        wizardSteps.push({
          id: 'groups',
          title: familiesChange.featureName || 'Grupos Familiares',
          description: `Selecciona ${familiesChange.newLimit === 1 ? 'tu grupo' : `${familiesChange.newLimit} grupos`}`,
          component: 'groups',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de miembros si necesita selección
      const membersChange = comp.changes.find(c => c.feature === 'members');
      console.log('👤 Members change:', membersChange);
      if (membersChange?.needsSelection) {
        wizardSteps.push({
          id: 'members',
          title: membersChange.featureName || 'Integrantes',
          description: `Selecciona ${membersChange.newLimit === 1 ? '1 integrante' : `${membersChange.newLimit} integrantes`}`,
          component: 'members',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de actividades si necesita selección
      const activitiesChange = comp.changes.find(c => c.feature === 'activities');
      console.log('🏃 Activities change:', activitiesChange);
      console.log('🏃 Activities needsSelection:', activitiesChange?.needsSelection);
      console.log('🏃 Activities newLimit:', activitiesChange?.newLimit);
      console.log('🏃 Activities selectionLimit:', activitiesChange?.selectionLimit);
      if (activitiesChange?.needsSelection) {
        wizardSteps.push({
          id: 'activities',
          title: activitiesChange.featureName || 'Actividades',
          description: `Selecciona ${activitiesChange.newLimit} actividades`,
          component: 'activities',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de checklists si necesita selección
      const checklistsChange = comp.changes.find(c => c.feature === 'checklists');
      console.log('📋 Checklists change:', checklistsChange);
      if (checklistsChange?.needsSelection) {
        wizardSteps.push({
          id: 'checklists',
          title: checklistsChange.featureName || 'Tareas y Checklists',
          description: `Selecciona ${checklistsChange.newLimit} tareas`,
          component: 'checklists',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de recetas si necesita selección
      const recipesChange = comp.changes.find(c => c.feature === 'recipes');
      console.log('🍳 Recipes change:', recipesChange);
      if (recipesChange?.needsSelection) {
        wizardSteps.push({
          id: 'recipes',
          title: recipesChange.featureName || 'Recetas',
          description: `Selecciona ${recipesChange.newLimit} recetas`,
          component: 'recipes',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de lista de compras si necesita selección
      const shoppingListsChange = comp.changes.find(c => c.feature === 'shoppingLists');
      console.log('🛒 Shopping Lists change:', shoppingListsChange);
      if (shoppingListsChange?.needsSelection) {
        wizardSteps.push({
          id: 'shopping',
          title: shoppingListsChange.featureName || 'Listas de Compras',
          description: `Selecciona ${shoppingListsChange.newLimit} lista${shoppingListsChange.newLimit > 1 ? 's' : ''}`,
          component: 'shopping',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de features bloqueadas si hay alguna
      const hasBlockedFeatures = comp.changes.some(c => c.status === 'blocked' || c.status === 'limited_to_blocked');
      if (hasBlockedFeatures) {
        wizardSteps.push({
          id: 'blocked',
          title: 'Features Pausadas',
          description: 'Información sobre funciones que se pausarán',
          component: 'blocked',
          canSkip: false,
          isRequired: true,
        });
      }
      
      // Agregar paso de resumen
      wizardSteps.push({
        id: 'summary',
        title: 'Resumen de Cambios',
        description: 'Revisa y confirma tus selecciones',
        component: 'summary',
        canSkip: false,
        isRequired: true,
      });
      
      console.log('✅ PASOS FINALES GENERADOS:', wizardSteps.map(s => ({
        id: s.id,
        title: s.title,
        component: s.component
      })));
      
      setSteps(wizardSteps);
    } catch (err: any) {
      console.error('Error inicializando wizard:', err);
      setError(err.message || 'Error al inicializar el wizard');
    } finally {
      setLoading(false);
    }
  }, [activeFamily?.id, user, fromPlanId, toPlanId]);
  
  /**
   * Abre el wizard
   */
  const open = useCallback(() => {
    setIsOpen(true);
    if (!comparison) {
      initialize();
    }
  }, [comparison, initialize]);
  
  /**
   * Cierra el wizard
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setCurrentStepIndex(0);
  }, []);
  
  /**
   * Navega al siguiente paso
   */
  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [currentStepIndex, steps.length]);
  
  /**
   * Navega al paso anterior
   */
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);
  
  /**
   * Actualiza una selección específica
   */
  const updateSelection = useCallback((key: keyof DowngradeSelections, value: any) => {
    setSelections(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);
  
  /**
   * Valida que las selecciones sean correctas
   */
  const validateSelections = useCallback((): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!comparison) {
      errors.push('No se pudo cargar la comparación de planes');
      return { valid: false, errors };
    }
    
    // Validar actividades
    const activitiesChange = comparison.changes.find(c => c.feature === 'activities');
    if (activitiesChange?.needsSelection) {
      const selectedCount = selections.activeActivityIds?.length || 0;
      const required = activitiesChange.newLimit;
      if (selectedCount !== required) {
        errors.push(`Debes seleccionar exactamente ${required} actividades (seleccionadas: ${selectedCount})`);
      }
    }
    
    // Validar checklists
    const checklistsChange = comparison.changes.find(c => c.feature === 'checklists');
    if (checklistsChange?.needsSelection) {
      const selectedCount = selections.activeChecklistIds?.length || 0;
      const required = checklistsChange.newLimit;
      if (selectedCount !== required) {
        errors.push(`Debes seleccionar exactamente ${required} checklists (seleccionados: ${selectedCount})`);
      }
    }
    
    // Validar recetas
    const recipesChange = comparison.changes.find(c => c.feature === 'recipes');
    if (recipesChange?.needsSelection) {
      const selectedCount = selections.activeRecipeIds?.length || 0;
      const required = recipesChange.newLimit;
      if (selectedCount !== required) {
        errors.push(`Debes seleccionar exactamente ${required} recetas (seleccionadas: ${selectedCount})`);
      }
    }
    
    // Validar lista de compras
    const shoppingListsChange = comparison.changes.find(c => c.feature === 'shoppingLists');
    if (shoppingListsChange?.needsSelection) {
      if (!selections.activeShoppingListId) {
        errors.push('Debes seleccionar 1 lista de compras');
      }
    }
    
    return { valid: errors.length === 0, errors };
  }, [comparison, selections]);
  
  /**
   * Aplica el downgrade con las selecciones del usuario
   */
  const applyDowngrade = useCallback(async () => {
    if (!activeFamily?.id || !user) {
      setError('No hay familia o usuario activo');
      return false;
    }
    
    // Validar selecciones
    const validation = validateSelections();
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 1. Crear snapshot
      const snapshotId = await createDowngradeSnapshot(
        user.uid,
        activeFamily.id,
        fromPlanId,
        selections
      );
      
      // 2. Pausar contenido no seleccionado
      await pauseUnselectedContent(activeFamily.id, selections);
      
      // 3. ⭐ ACTUALIZAR EL PLAN DEL USUARIO A FREE
      const { doc, updateDoc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/firebaseConfig');
      
      // Actualizar documento del usuario
      await updateDoc(doc(db, 'users', user.uid), {
        'subscription.plan': toPlanId,
        'subscription.downgradedAt': new Date(),
        'subscription.updatedAt': new Date(),
      });
      
      // ⭐ Limpiar localStorage para que detecte upgrade cuando renueve
      localStorage.setItem(`previousPlan_${user.uid}`, toPlanId);
      
      // 4. ⭐ ACTUALIZAR SUBSCRIPTION_ACCOUNTS para aplicar límites
      const accountRef = doc(db, 'subscription_accounts', user.uid);
      const accountSnap = await getDoc(accountRef);
      
      if (accountSnap.exists()) {
        // Obtener límites del plan target desde Firestore
        const targetPlanRef = doc(db, 'subscriptionPlans', toPlanId);
        const targetPlanSnap = await getDoc(targetPlanRef);
        
        if (targetPlanSnap.exists()) {
          const targetPlanData = targetPlanSnap.data();
          const newLimits = targetPlanData.limits || {};
          const newFamilyQuota = targetPlanData.familyQuota || 1;
          
          await updateDoc(accountRef, {
            plan: toPlanId,
            limits: newLimits,
            familyQuota: newFamilyQuota,
            billingStatus: 'free',
            updatedAt: new Date(),
          });
        }
      }
      
      return true;
    } catch (err: any) {
      console.error('Error aplicando downgrade:', err);
      setError(err.message || 'Error al aplicar el downgrade');
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFamily?.id, user, fromPlanId, toPlanId, selections, validateSelections]);
  
  // Paso actual
  const currentStep = steps[currentStepIndex];
  
  // Flags de navegación
  const canGoBack = currentStepIndex > 0;
  const canGoNext = currentStepIndex < steps.length - 1;
  const isLastStep = currentStepIndex === steps.length - 1;
  
  return {
    // Estado
    isOpen,
    loading,
    error,
    
    // Datos
    comparison,
    currentCounts,
    recommendations,
    selections,
    
    // Pasos
    steps,
    currentStep,
    currentStepIndex,
    canGoBack,
    canGoNext,
    isLastStep,
    
    // Acciones
    open,
    close,
    initialize,
    nextStep,
    prevStep,
    updateSelection,
    validateSelections,
    applyDowngrade,
  };
}
